import { getFolderIdFromMyMaps, gpxLog } from "~/gpx/util";
import Dialog from "~/ui/dialog";
import { store } from "~/state/store";
import { _ } from "~/i18n";
import Notifications, { NOTIFICATIONS_TYPE } from "~/util/notifications";
import { addActiveContent, removeActiveContent } from "~/state/content/state";
import { icon, node } from "~/util/dom";
import { IImportResponse, parseFiles } from "~/es6/provider-gpx";

// import
class GPXImport {
	_dom: {
		container?: HTMLDivElement;
		uploadArea?: HTMLLabelElement;
		cover?: HTMLDivElement;
		closeBtn?: HTMLButtonElement;
		loader?: HTMLDivElement;
		error?: HTMLParagraphElement;
	};

	_dataConf: {
		logType: string;
		folderId: string;
		skipAddDialog: boolean;
		files: FileList;
		folderTitle: string;
	};

	_files: Array<File>;

	constructor() {
		this._dom = {};
		this._files = [];
		this._dataConf = null;
	}

	get filesName() {
		return this._files.map(item => item.name).join(", ");
	}

	open(optsArg) {
		const opts = {
			files: null,
			folderId: "",
			folderTitle: "",
			detectFolder: false, // detekce folderId - pak se skipne folderId
			logType: "", // analytika
			skipAddDialog: false, // preskocit pridavaci dialog -> zrovna pridat
			...optsArg,
		};

		this._dataConf = {
			files: opts.files,
			folderId: opts.folderId,
			folderTitle: opts.folderTitle,
			logType: opts.logType,
			skipAddDialog: opts.skipAddDialog,
		};

		if (opts.detectFolder) {
			const folderId = getFolderIdFromMyMaps();
			const folderTitle = Mapy.ProviderMapyBox.folderTitle(folderId);

			this._dataConf.folderId = folderId;
			this._dataConf.folderTitle = folderTitle;
		}

		// postavime login
		const gpxHasData = Mapy.getComponent("gpx").hasData();
		const { user } = store.getState().user;

		if (user.state === "green") {
			this._open();
		} else {
			// analytika
			if (!gpxHasData) {
				this._logOpen();
			}
			// musime se prihlasit
			Mapy.getComponent("login").open({
				analyticsData: {
					loginOrigin: "import",
					section: "import",
				},
				motivation: "gpx",
			}).then(() => {
				this._open();
			}, () => {});
		}
	}

	clear() {
		this._files.length = 0;
	}

	close() {
		store.dispatch(removeActiveContent("gpx-import"));
		this._dataConf.skipAddDialog = false;

		if (!this._dom.container) {
			return;
		}

		this._dom.closeBtn.removeEventListener("click", this);
		this._dom.uploadArea.removeEventListener("drop", this);
		this._dom.uploadArea.removeEventListener("dragover", this);
		this._dom.uploadArea.removeEventListener("dragenter", this);
		this._dom.uploadArea.removeEventListener("dragleave", this);
		this._removeError();
		this._dom.container.remove();
		this._dom.cover.remove();
		this._dom.container = null;
		document.body.removeEventListener("keydown", this);
		document.body.style.overflow = "";
	}

	handleEvent(event: Event) {
		switch (event.type) {
			case "click":
				if (this._dom.closeBtn.contains(event.target as Node)) {
					this.close();
				}
				break;

			case "keydown":
				if ((event as KeyboardEvent).which === 27) {
					this.close();
				}
				break;

			case "dragover":
				event.preventDefault();
				this._dom.uploadArea.classList.add("active");
				break;

			case "dragenter":
				this._dom.uploadArea.classList.add("active");
				break;

			case "dragleave":
				this._dom.uploadArea.classList.remove("active");
				break;

			// drop na misto v dialogu - drop do mapy je v provider map
			case "drop":
				event.preventDefault();
				event.stopPropagation();

				this._dom.uploadArea.classList.remove("active");

				if ((event as InputEvent).dataTransfer && (event as InputEvent).dataTransfer.files) {
					// znovu otevreme pres open, pouzijeme folderId, vynutime login pro neprihlasene
					this._dataConf.files = (event as InputEvent).dataTransfer.files;
					this._dataConf.logType = "dragDrop";

					this._open();
				}
				break;

			case "change":
				if ((event.target as HTMLInputElement).files.length) {
					// znovu otevreme pres open, pouzijeme folderId, vynutime login pro neprihlasene
					this._dataConf.files = (event.target as HTMLInputElement).files;
					this._dataConf.logType = "dragDrop";

					this._open();
				}
				break;

			default:
				//
		}
	}

	_open() {
		const gpxCont = Mapy.getComponent("gpx");

		store.dispatch(addActiveContent("gpx-import"));

		// nemame soubory, jiz mame nacteno -> zobrazime
		if (!this._dataConf.files) {
			if (gpxCont.hasData() && !gpxCont.isActive()) {
				gpxLog("import_open_last");
				gpxCont.activate();

				return;
			}
		}

		// mame vstupni soubory
		if (this._dataConf.files) {
			// mame jiz nactene soubory
			if (this._files.length) {
				// skipneme dialog - vytvorime zrovna popup a zacneme nacitat soubory
				if (this._dataConf.skipAddDialog) {
					this._openDialogWithFiles();
				} else {
					// replace dialog s volbama
					this._createReplaceDialog();
				}
			} else {
				// nemame data, zobrazime dialog a nacitame soubory
				this._openDialogWithFiles();
			}
		} else {
			// nemame soubory - vytvorime dialog pro zadani souboru
			this._create();
		}
	}

	_openDialogWithFiles() {
		// vytvorime a pridame loader
		this._create();
		this._removeError();
		this._dom.container.appendChild(this._dom.loader);
		// nacteme soubory
		this._processFiles().then(data => {
			const gpxCont = Mapy.getComponent("gpx");
			const skipAddDialog = this._dataConf.skipAddDialog;

			this._dom.loader.remove();
			this.close();

			// pridame data
			if (skipAddDialog) {
				gpxCont.addData(data);
			} else {
				// aktivujeme content
				gpxCont.activate();
				// nastavime data
				gpxCont.setState({
					gpxData: data,
					folderTitle: this._dataConf.folderTitle,
					folderId: this._dataConf.folderId,
				});
			}
		}, ex => {
			this._dom.loader.remove();
			this._createError(ex.msg);
		});
	}

	_processFiles(): Promise<IImportResponse> {
		return new Promise((resolve, reject) => {
			const allFileNames = this._files.map(item => item.name);
			const newFiles = [];

			// test na gpx
			for (let index = 0, max = this._dataConf.files.length; index < max; index++) {
				const file = this._dataConf.files[index];

				if (file.name.toLowerCase().indexOf(".gpx") === -1) {
					reject({
						e: null,
						msg: `${_("gpx.import.fileExtError", { file: file.name })}<strong>${_("gpx.import.fileExt")}</strong>`,
					});

					return;
				} else if (allFileNames.indexOf(file.name) !== -1) {
					reject({
						e: null,
						msg: _("gpx.import.fileDuplicate"),
					});

					return;
				}

				newFiles.push(file);
			}

			this._files = this._files.concat(newFiles);

			// backend
			parseFiles(this._files).then(data => {
				// test na import pouze routy
				if (!data.data.points.length && !data.data.tracks.length && data.warnings.filter(item => item.key === "route_not_supported").length > 0) {
					// smazeme soubory
					this.clear();
					reject({
						e: null,
						msg: _("gpx.import.onlyRoute"),
					});

					return;
				}

				// analytika
				gpxLog("import_files", {
					files: {
						/* eslint-disable camelcase */
						num_points: data.data.points.length,
						num_paths: data.data.tracks.length,
						num_routes: 0,
						/* eslint-enable camelcase */
					},
				});

				// warningy
				if (data.maxPointsWarning) {
					Notifications.notify({
						content: _("gpx.import.importMaxCount", {
							count: data.maxPointsCount,
						}),
						duration: 10000,
						type: NOTIFICATIONS_TYPE.INFO,
					});
				}

				data.warnings.forEach(warning => {
					let msg = "";

					switch (warning.key) {
						case "route_not_supported":
							Notifications.notify({
								content: _("gpx.import.routeSkip", { NUM: warning.count }),
								duration: 10000,
								type: NOTIFICATIONS_TYPE.INFO,
							});
							break;
						case "parse_point_failed":
							msg = _("gpx.import.warn.parsePointFailed");
							break;
						case "parse_route_failed":
							msg = _("gpx.import.warn.parseRouteFailed");
							break;
						case "parse_track_failed":
							msg = _("gpx.import.warn.parseTrackFailed");
							break;
						case "partial_import":
							msg = _("gpx.import.warn.partialImport");
							break;
						case "max_entities_reached":
							msg = _("gpx.import.warn.maxEntitiesReached");
							break;
						case "some_file_empty":
							msg = _("gpx.import.warn.someFileEmpty", {
								NUM: warning.count,
							});
							break;
						default:
							msg = warning.key;
					}

					if (msg) {
						Notifications.notify({
							content: msg,
							type: NOTIFICATIONS_TYPE.INFO,
						});
					}
				});

				// data
				resolve(data);
			}, ex => {
				// smazeme soubory
				this.clear();
				// vsechny klice maji stejnou hlasku (e.data.error) - unrecognized_format, unrecognized_format_xml, file_empty, data_too_short
				reject({
					ex,
					msg: ex.status === 413 ? _("reportDefect.form.fileSizeError", { size: 41 }) : _("gpx.import.fileInvalidError"),
				});
			});
		});
	}

	_create() {
		if (this._dom.container) {
			return;
		}

		const container = node("div", { className: "gpx-import" });
		const cover = node("div", { className: "gpx-import-cover" });
		const closeBtn = node("button", { type: "button", className: "close-btn" });
		const title = node("h2", { className: "title", textContent: _("gpx.import.title") });
		const uploadArea = node("label", { className: "upload-area" });
		const plus = node("span", { className: "plus" });
		const uploadTitle = node("h3", { className: "title", textContent: _("gpx.import.upload") });
		const uploadInput = node("input", { type: "file" });
		const loader = node("div", { className: "loader" });
		const loaderImg = new Image();

		closeBtn.appendChild(icon("close"));
		closeBtn.appendChild(node("span", { textContent: _("close") }));
		closeBtn.addEventListener("click", this);
		uploadInput.setAttribute("multiple", "true");
		uploadInput.addEventListener("change", this);
		container.appendChild(closeBtn);
		container.appendChild(title);
		container.appendChild(uploadArea);
		uploadArea.appendChild(plus);
		uploadArea.appendChild(uploadTitle);
		uploadArea.appendChild(uploadInput);
		document.body.appendChild(cover);
		document.body.appendChild(container);
		loaderImg.src = "/img/loader.gif";
		loaderImg.crossOrigin = "anonymous";
		loader.appendChild(loaderImg);

		uploadArea.addEventListener("drop", this);
		uploadArea.addEventListener("dragover", this);
		uploadArea.addEventListener("dragenter", this);
		uploadArea.addEventListener("dragleave", this);

		document.body.addEventListener("keydown", this);
		document.body.style.overflow = "hidden";

		Object.assign(this._dom, { container, cover, closeBtn, uploadArea, loader });

		// analytika
		this._logOpen();
	}

	_createError(ownMsg) {
		const error = node("p", { className: "error" });

		if (ownMsg) {
			error.innerHTML = ownMsg;
			error.classList.add("ext-error");
		} else {
			error.textContent = _("gpx.import.uploadError");
		}

		this._dom.error = error;
		this._dom.container.appendChild(error);
	}

	_createReplaceDialog() {
		// zavreme puvodni dialog -> vse se deje v novem dialogu
		this.close();
		// dialog pro pridani gpx souboru
		gpxLog("import_overwrite_dialog");
		const gpxCont = Mapy.getComponent("gpx");
		const errorP = node("p", { className: "error" });
		const loaderImg = node("img", { src: "/img/loader.gif", className: "loader-img" });
		const dialog = new Dialog({
			className: "import-gpx-dialog",
			title: _("gpx.content.addToImport"),
			desc: this.filesName,
			logSection: "import",
		});

		dialog.addBtn({
			title: _("add"),
			bindEnter: true,
			type: "ok",
			cb: () => {
				dialog.dom.container.classList.add("loading");
				errorP.innerHTML = "";

				// nacteme soubory
				this._processFiles().then(data => {
					dialog.dom.container.classList.remove("loading");
					dialog.close();
					// pridame data
					gpxCont.activate();
					gpxCont.addData(data);
				}, ex => {
					dialog.dom.container.classList.remove("loading");
					errorP.innerHTML = ex.msg;
				});
			},
		});
		dialog.addBtn({
			title: _("replace"),
			cb: () => {
				this.clear();
				errorP.innerHTML = "";

				gpxLog("import_overwrite_confirm");
				dialog.dom.container.classList.add("loading");
				// nacteme soubory
				this._processFiles().then(data => {
					dialog.dom.container.classList.remove("loading");
					dialog.close();
					gpxCont.activate();
					gpxCont.clear();
					// aktivujeme content
					gpxCont.setState({
						gpxData: data,
						folderTitle: this._dataConf.folderTitle,
						folderId: this._dataConf.folderId,
					});
				}, ex => {
					dialog.dom.container.classList.remove("loading");
					errorP.innerHTML = ex.msg;
				});
			},
		});
		dialog.addBtn({
			title: _("cancel"),
			cb: () => {
				dialog.close();
			},
		});
		dialog.dom.content.appendChild(errorP);
		dialog.dom.buttons.appendChild(loaderImg);
		dialog.open();
	}

	_removeError() {
		if (this._dom.error) {
			this._dom.error.remove();
			this._dom.error = null;
		}
	}

	_logOpen() {
		if (!this._dataConf.logType) {
			return;
		}

		gpxLog("import_open", {
			type: this._dataConf.logType,
		});
	}
}

export default new GPXImport();
